import { Small } from "@fronterahealth/frontera-ui-components";
import { cn } from "@fronterahealth/frontera-ui-components/lib/utils";
import {
  InputGroup,
  InputGroupButton,
  InputGroupTextarea,
} from "@fronterahealth/frontera-ui-components/ui/input-group";

import { submitOrCancelOnKey } from "@components/QaReview/components/commentKeyboard";
import { useCreateComment } from "@components/QaReview/hooks/useCreateComment";
import { UNNAMED_FIELD } from "@components/QaReview/hooks/useReviewAnchors";
import { ReviewComposer, useCommentDraft, useReviewSession } from "@components/QaReview/hooks/useReviewSession";

interface CommentComposerProps {
  target: ReviewComposer;
  /** Sits inside a thread card, so it drops its own border, background, shadow, rounding and padding. */
  embedded?: boolean;
}

export const CommentComposer: React.FC<CommentComposerProps> = ({ target, embedded = false }) => {
  const { dispatch } = useReviewSession();
  const createComment = useCreateComment();
  // The composer slot is reused when it moves to another field, so this follows the target's draft.
  const [draft, setDraft] = useCommentDraft(target);

  const close = () => dispatch({ type: "closeComposer" });
  const fieldTitle = target.kind === "comment" ? target.fieldTitle.trim() : "";

  const submit = () => {
    if (!draft.trim()) return;
    createComment(target, draft.trim());
    setDraft("");
    close();
  };

  return (
    <div
      data-testid="comment-composer"
      className={cn(
        "space-y-2",
        !embedded && "rounded-lg border border-dashed border-blurple-300 bg-white p-3 shadow-sm",
      )}
    >
      {target.kind === "comment" && (
        <Small className="text-text-tertiary">
          {fieldTitle ? `New comment on "${fieldTitle}"` : `New comment on ${UNNAMED_FIELD}`}
        </Small>
      )}
      <InputGroup>
        <InputGroupTextarea
          placeholder={target.kind === "comment" ? "Leave a comment…" : "Write a reply…"}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={submitOrCancelOnKey(submit, close)}
          rows={2}
          autoFocus
        />
        {/* The group centers its items; mt-2 matches the textarea's py-2 so Send lines up with the first line. */}
        <InputGroupButton className="mt-2 self-start" onClick={submit}>
          Send
        </InputGroupButton>
      </InputGroup>
      {target.kind === "comment" && (
        <button type="button" className="text-xs font-medium text-text-secondary" onClick={close}>
          Cancel
        </button>
      )}
    </div>
  );
};
